import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import type { JSX } from 'react';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { PageLoader } from '@/components/common/PageLoader';
import { DeferredTawkChat } from '@/components/common/DeferredTawkChat';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Paywall } from '@/components/billing';
import { useAuthStore } from '@/stores';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useOAuthRedirectResult } from '@/hooks/useOAuthRedirectResult';
import {
  Login,
  Signup,
  ForgotPassword,
  VerifyEmail,
  ResetPassword,
  OAuthCallback,
  OnboardingFlow,
  AutomationPage,
} from './routes';

export default function App(): JSX.Element {
  const { hasCompletedOnboarding, hasSkippedOnboarding } = useAuthStore();

  useAuthRedirect();
  // Return path of a social connect. Mounted here rather than in AppLayout
  // because a connect can also be started from the onboarding flow, which
  // renders instead of the app shell (LIL-490).
  useOAuthRedirectResult();

  useEffect(() => {
    document.title = 'LiLi - Own The Feed, Own the Field';
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Redirect root to sign-in */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          <Route path="/login" element={<Login />} />

          <Route path="/signup" element={<Signup />} />

          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/auth/callback" element={<OAuthCallback />} />

          {/* Full-screen subscription wall. Reached from the in-app upgrade
              prompts (locked feature → UpgradeModal / LockedFeature CTA). Sits
              above /app/* so it renders without the AppLayout chrome. */}
          <Route
            path="/app/upgrade"
            element={
              <ProtectedRoute>
                <Paywall onBack={() => window.history.back()} />
              </ProtectedRoute>
            }
          />

          {/* Post-checkout landing — Stripe `success_url` points here. Polls the
              subscription active, starts/resumes automation, then tracks it.
              Full-screen (above /app/*) and works mid-onboarding too. */}
          <Route
            path="/app/automation"
            element={
              <ProtectedRoute>
                <AutomationPage />
              </ProtectedRoute>
            }
          />

          {/* Protected app — browsable shell. Premium features are gated
              per-route inside AppLayout (FeatureGate), not app-wide. */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <>
                  {!(hasCompletedOnboarding || hasSkippedOnboarding) ? (
                    <OnboardingFlow />
                  ) : (
                    <AppLayout />
                  )}
                  <DeferredTawkChat />
                </>
              </ProtectedRoute>
            }
          />

          {/* fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
